import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import RadioInput from 'LuxKit/Radio/RadioInput'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import AssistChip from 'LuxKit/Chips/AssistChip'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineCalendarAltIcon from 'LuxKit/Icons/line/LineCalendarAltIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import { allCountries } from 'country-telephone-data'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { FormattedMessage, useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { LeLiveRegistration } from 'leLive/api/leLive'
import LeLiveAddToCalendar from './LeLiveAddToCalendar'
import styled from 'styled-components'
import { rem } from 'polished'

const CardContainer = styled.div`
  background-color: #F3F1EA;
  border-radius: ${rem(16)};
  padding: ${rem(28)};
  box-shadow: 0 ${rem(20)} ${rem(25)} rgba(0, 0, 0, 0.1);
`

const CardLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(9)} 0;
`

const SuccessBanner = styled(Sheet)`
  && { background-color: ${props => props.theme.palette.highlight.primary.normalBackground}; }
`

const ChipSelect = styled.div`
  position: relative;

  select {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
`

export type EventDay = 'saturday' | 'sunday'

export interface RegistrationFormValues {
  ticketQuantity: number
  eventDay: EventDay
  marketingOptIn: boolean
  phoneNumber?: string
  guestEmail?: string
}

interface Props {
  onSubmit: (values: RegistrationFormValues) => Promise<void> | void
  isSubmitting: boolean
  isLoading?: boolean
  registration: LeLiveRegistration | undefined
  error?: string
}

function CardLogo() {
  const intl = useIntl()
  const leLiveEventName = intl.formatMessage({
    id: 'qcBVmIRI7c4oTpUWyYl1',
    defaultMessage: 'Luxury Escapes Live 2026',
    description: 'Accessible event name used for the LE Live 2026 logo in the registration form',
  })

  return (
    <CSSBreakpoint min="desktop">
      <CardLogoWrapper>
        <Image
          id="f3tsulfa56cg8fntg87"
          alt={leLiveEventName}
          width={200}
          height={100}
          dpr={2}
          enhancedFormats
        />
      </CardLogoWrapper>
    </CSSBreakpoint>
  )
}

function getEventDayFromApi(apiDay: string): EventDay {
  return apiDay === 'DAY_ONE' ? 'saturday' : 'sunday'
}

function LeLiveRegistrationForm(props: Props) {
  const { onSubmit, isSubmitting, isLoading, registration, error } = props
  const intl = useIntl()
  const eventDayLabels: Record<EventDay, string> = {
    saturday: intl.formatMessage({
      id: 'CgKcHtNBfhrwfXHmDqH9',
      defaultMessage: 'Saturday',
      description: 'Day name label for Saturday event day selection in LE Live registration',
    }),
    sunday: intl.formatMessage({
      id: 'G5gWpOfNxKT67fPa6lVV',
      defaultMessage: 'Sunday',
      description: 'Day name label for Sunday event day selection in LE Live registration',
    }),
  }
  const loggedIn = useAppSelector(selectLoggedIn)

  const [isEditing, setIsEditing] = useState(false)
  const [ticketQuantity, setTicketQuantity] = useState(1)
  const [eventDay, setEventDay] = useState<EventDay>('saturday')
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const [guestEmail, setGuestEmail] = useState('')
  const [phonePrefix, setPhonePrefix] = useState('61')
  const [phoneNumber, setPhoneNumber] = useState('')
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const isGuestMode = !loggedIn

  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setTicketQuantity(Number(e.currentTarget.value))
  }, [])

  const handleSaturdayChange = useCallback(() => {
    setEventDay('saturday')
  }, [])

  const handleSundayChange = useCallback(() => {
    setEventDay('sunday')
  }, [])

  const handleMarketingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMarketingOptIn(e.currentTarget.checked)
  }, [])

  const handleGuestEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestEmail(e.currentTarget.value)
  }, [])

  const handlePrefixChange = useCallback(({ phonePrefix: prefix }: { phonePrefix: string }) => {
    setPhonePrefix(prefix)
    setPhoneNumber('')
  }, [])

  const handlePhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.currentTarget.value)
  }, [])

  const dialCodes = useMemo(() =>
    [...new Set(allCountries.map(c => c.dialCode))].sort((a, b) => b.length - a.length),
  [])

  const handleChange = useCallback(() => {
    if (registration) {
      setEventDay(getEventDayFromApi(registration.eventDay))
      setTicketQuantity(registration.ticketQuantity)
      setMarketingOptIn(registration.marketingOptIn)
      if (registration.phoneNumber) {
        const digits = registration.phoneNumber.replace(/^\+/, '')
        const matched = dialCodes.find(code => digits.startsWith(code))
        if (matched) {
          setPhonePrefix(matched)
          setPhoneNumber(digits.slice(matched.length))
        } else {
          setPhoneNumber(digits)
        }
      }
    }
    setIsEditing(true)
  }, [registration, dialCodes])

  // Required for new registrations and when editing a legacy record that has no phone (5,127 rows).
  // Optional on edit when the record already has a phone number.
  const isPhoneRequired = !registration || (isEditing && !registration.phoneNumber)

  const isPhoneInvalid = phoneNumber.trim() !== '' && phoneInputRef.current !== null && !phoneInputRef.current.checkValidity()

  const handleSubmit = useCallback(async() => {
    if (isPhoneRequired && !phoneNumber.trim()) return
    if (phoneNumber.trim() && phoneInputRef.current && !phoneInputRef.current.checkValidity()) return

    const digits = phoneNumber.replace(/\s/g, '')
    const combinedPhone = digits ? `+${phonePrefix}${digits}` : undefined

    if (isGuestMode) {
      if (!guestEmail.trim()) return
      try {
        await onSubmit({ ticketQuantity, eventDay, marketingOptIn, phoneNumber: combinedPhone, guestEmail: guestEmail.trim() })
        setIsEditing(false)
      } catch {
        // Parent sets submitError; keep form visible so the error banner is shown
      }
      return
    }

    try {
      await onSubmit({ ticketQuantity, eventDay, marketingOptIn, phoneNumber: combinedPhone })
      setIsEditing(false)
    } catch {
      // Parent sets submitError; keep form visible so the error banner is shown
    }
  }, [isPhoneRequired, phoneNumber, phonePrefix, isGuestMode, guestEmail, onSubmit, ticketQuantity, eventDay, marketingOptIn])

  const phoneLabel = intl.formatMessage({
    id: 'CCDBxY4y8-HxdPjXkKmQ',
    defaultMessage: 'Phone number',
    description: 'Label for phone number input in LE Live registration form',
  })

  const phoneRequiredError = intl.formatMessage({
    id: '8h1f2-r27PpUxBWgRQeV',
    defaultMessage: 'Please fill in this field.',
    description: 'Validation error shown when phone number is missing in LE Live registration form',
  })

  const submitLabel = intl.formatMessage({
    id: 'lhjfEvzm5HsRBtoVaFiq',
    defaultMessage: 'Get tickets',
    description: 'Submit button label for LE Live registration form in the LE Live events section',
  })

  const submittingLabel = intl.formatMessage({
    id: 'CmoEyabgzFRLZeuOFTus',
    defaultMessage: 'Registering…',
    description: 'Action button label displayed while LE Live form is submitting',
  })

  if (isLoading) {
    return (
      <CardContainer>
        <Group direction="vertical" gap={20}>
          <CardLogo />
          <LoadingBox height={20} borderRadius="S" />
          <LoadingBox height={20} borderRadius="S" />
          <LoadingBox height={20} borderRadius="S" />
          <LoadingBox height={48} borderRadius="M" />
          <LoadingBox height={48} borderRadius="M" />
          <LoadingBox height={48} borderRadius="L" />
        </Group>
      </CardContainer>
    )
  }

  if (registration && !isEditing) {
    const registeredDay = eventDayLabels[getEventDayFromApi(registration.eventDay)]

    return (
      <CardContainer>
        <Group direction="vertical" gap={20}>
          <CardLogo />

          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" startIcon={<LineTicketIcon />}>
              {intl.formatMessage({
                id: 'U4AlqFdlpRf-Na_VfC5K',
                defaultMessage: 'FREE entry for members',
                description: 'Ticket pricing info for LE Live event in the LE Live events section',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineCalendarAltIcon />}>
              {intl.formatMessage({
                id: 'DWCIdwA0ifE47FzNmCN_',
                defaultMessage: 'Sat 16 May & Sun 17 May 2026',
                description: 'Event dates for LE Live in the LE Live events section',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />}>
              {intl.formatMessage({
                id: 'a_xhG3Gbc98p7AQH04eJ',
                defaultMessage: 'Royal Exhibition Building, Melbourne',
                description: 'Event location for LE Live in the LE Live events section',
              })}
            </BodyTextBlock>
          </Group>

          <SuccessBanner borderRadius="L" padding={16}>
            <Group direction="vertical" gap={12}>
              <Group direction="horizontal" gap={12} verticalAlign="start">
                <SolidCheckCircleIcon />
                <BodyText variant="medium">
                  <FormattedMessage
                    id="itIhyqWz2uaAjNZucX4q"
                    defaultMessage="<strong>{greeting}we've sent you {ticketQuantity, plural, one {# ticket} other {# tickets}} for {day}!</strong> Check your inbox for your Luxury Escapes Live email confirmation. <changeLink>Change</changeLink>"
                    description="Success message after LE Live registration with ticket count, day, confirmation instructions, and change link"
                    values={{
                      greeting: registration.firstName ? `${registration.firstName}, ` : '',
                      ticketQuantity: registration.ticketQuantity,
                      day: registeredDay,
                      strong: (chunks) => <strong>{chunks}</strong>,
                      changeLink: (chunks) => <TextLink onClick={handleChange} size="medium" weight="bold">{chunks}</TextLink>,
                    }}
                  />
                </BodyText>
              </Group>
              <LeLiveAddToCalendar
                eventDay={registration.eventDay}
                kind="tertiary"
                size="small"
                fit="auto"
              />
            </Group>
          </SuccessBanner>
        </Group>
      </CardContainer>
    )
  }

  return (
    <CardContainer id="registration">
      <Group direction="vertical" gap={20}>
        <CardLogo />
        <Group direction="vertical" gap={8}>
          <BodyTextBlock variant="medium" startIcon={<LineTicketIcon />}>
            {intl.formatMessage({
              id: 'U4AlqFdlpRf-Na_VfC5K',
              defaultMessage: 'FREE entry for members',
              description: 'Ticket pricing info for LE Live event in the LE Live events section',
            })}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineCalendarAltIcon />}>
            {intl.formatMessage({
              id: 'DWCIdwA0ifE47FzNmCN_',
              defaultMessage: 'Sat 16 May & Sun 17 May 2026',
              description: 'Event dates for LE Live in the LE Live events section',
            })}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />}>
            {intl.formatMessage({
              id: 'a_xhG3Gbc98p7AQH04eJ',
              defaultMessage: 'Royal Exhibition Building, Melbourne',
              description: 'Event location for LE Live in the LE Live events section',
            })}
          </BodyTextBlock>
        </Group>

        <Group direction="vertical" gap={12}>
          <Sheet background="normal-background" borderRadius="L" padding="8 16">
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'pjKXOwCVa2oWR4XZ9J5M',
                  defaultMessage: 'Number of tickets:',
                  description: 'Form field label for ticket quantity selector in the LE Live events section',
                })}
              </BodyText>
              <ChipSelect>
                <AssistChip
                  variant="outline"
                  size="medium"
                  endIcon={<LineAngleDownIcon />}
                >
                  {ticketQuantity}
                </AssistChip>
                <select
                  value={String(ticketQuantity)}
                  onChange={handleQuantityChange}
                  aria-label={intl.formatMessage({
                    id: 'KjEQ0-o_4BneJMNiycZ6',
                    defaultMessage: 'Number of tickets',
                    description: 'Accessibility aria-label for ticket quantity selector',
                  })}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <option key={n} value={String(n)}>{n}</option>
                  ))}
                </select>
              </ChipSelect>
            </Group>
          </Sheet>

          <Sheet background="normal-background" borderRadius="L" padding={16}>
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" wrap="wrap" gap={20}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'xu5bMTrQHJ_D269E0zIT',
                  defaultMessage: 'Date:',
                  description: 'Form field label for event day selector in the LE Live events section',
                })}
              </BodyText>
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <RadioInput
                  name="eventDay"
                  value="saturday"
                  checked={eventDay === 'saturday'}
                  onChange={handleSaturdayChange}
                >
                  {intl.formatMessage({
                    id: 'l9AeI55zyixH2HPvmIUk',
                    defaultMessage: 'Sat, 16 May',
                    description: 'Saturday option for event day in the LE Live events section',
                  })}
                </RadioInput>
                <RadioInput
                  name="eventDay"
                  value="sunday"
                  checked={eventDay === 'sunday'}
                  onChange={handleSundayChange}
                >
                  {intl.formatMessage({
                    id: 'sQ8DbjEND2ZXivGwVjLz',
                    defaultMessage: 'Sun, 17 May',
                    description: 'Sunday option for event day in the LE Live events section',
                  })}
                </RadioInput>
              </Group>
            </Group>
          </Sheet>

          <CheckboxInput
            checked={marketingOptIn}
            onChange={handleMarketingChange}
          >
            {intl.formatMessage({
              id: 'zsbR2R8pezsXcIH15cZo',
              defaultMessage: 'I’d like to receive exclusive travel offers and updates from Luxury Escapes and partners.',
              description: 'Marketing opt-in checkbox label in the LE Live events section',
            })}
          </CheckboxInput>
        </Group>

        {isGuestMode && (
          <TextInput
            type="email"
            label={intl.formatMessage({
              id: 'BInwM_5J1pmqfdTb4Lly',
              defaultMessage: 'Email address',
              description: 'Label for guest email input in LE Live registration',
            })}
            value={guestEmail}
            onChange={handleGuestEmailChange}
            placeholder={intl.formatMessage({
              id: 'qPE4Y4X-K8_aoWJ2Cmsg',
              defaultMessage: 'Enter your email address',
              description: 'Placeholder text for guest email input in LE Live registration',
            })}
            required
          />
        )}

        <PhoneInput
          name="phone"
          label={phoneLabel}
          prefixCountry="AU"
          phonePrefix={phonePrefix}
          phoneNumber={phoneNumber}
          inputRef={phoneInputRef}
          onPrefixChange={handlePrefixChange}
          onNumberChange={handlePhoneNumberChange}
          required={isPhoneRequired}
          requiredErrorMessage={phoneRequiredError}
        />

        {error && (
          <MessageBanner kind="critical">
            {error}
          </MessageBanner>
        )}

        <TextButton
          kind="primary"
          size="large"
          fit="full-width"
          onClick={handleSubmit}
          disabled={isSubmitting || (isGuestMode && !guestEmail.trim()) || (isPhoneRequired && !phoneNumber.trim()) || isPhoneInvalid}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </TextButton>

      </Group>
    </CardContainer>
  )
}

export default LeLiveRegistrationForm
